import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { ShieldAlert, ArrowLeft, Send, Inbox } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getAllTickets, getTicket, postTicketMessage, patchTicketStatus } from "@/lib/afmApi";
import { fmtDate } from "@/lib/format";
import { Loading, SectionTitle } from "@/components/common";

const FILTERS = [
  { key: "", label: "Todos" },
  { key: "pending", label: "Pendientes" },
  { key: "in_progress", label: "En proceso" },
  { key: "resolved", label: "Resueltos" },
  { key: "closed", label: "Cerrados" },
];
const TSTATUS = { pending: "Pendiente", open: "Abierto", in_progress: "En proceso", resolved: "Resuelto", closed: "Cerrado" };
const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-black";
const fmtDur = (ms) => { if (!ms) return "—"; const m = Math.round(ms / 60000); if (m < 60) return `${m} min`; const h = Math.floor(m / 60); const d = Math.floor(h / 24); return d >= 1 ? `${d}d ${h % 24}h` : `${h}h ${m % 60}m`; };

export default function AdminSupport() {
  const { user } = useAuth();
  const [filter, setFilter] = useState("");
  const [tickets, setTickets] = useState([]);
  const [status, setStatus] = useState("loading");
  const [msg, setMsg] = useState("");
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [allTickets, setAllTickets] = useState([]);

  const isAdmin = user?.super_admin || ["admin", "distribution_admin", "superadmin", "staff"].includes(user?.role);

  const metrics = useMemo(() => {
    const open = allTickets.filter((t) => ["pending", "open", "in_progress"].includes(t.status)).length;
    const resolved = allTickets.filter((t) => ["resolved", "closed"].includes(t.status)).length;
    const durs = [];
    allTickets.forEach((t) => {
      const created = Date.parse(t.created_at);
      const firstAdmin = (t.messages || []).find((m) => m.from_role === "admin" || m.from_role === "staff");
      if (created && firstAdmin?.created_at) { const dt = Date.parse(firstAdmin.created_at) - created; if (dt > 0) durs.push(dt); }
    });
    const avg = durs.length ? durs.reduce((a, b) => a + b, 0) / durs.length : 0;
    return { open, resolved, total: allTickets.length, avg };
  }, [allTickets]);

  const load = useCallback(() => {
    if (!isAdmin) { setMsg("Solo administradores de distribución."); setStatus("denied"); return; }
    setStatus("loading");
    getAllTickets(user.id, filter)
      .then((d) => { setTickets(Array.isArray(d) ? d : (d?.tickets || [])); setStatus("ok"); })
      .catch((e) => { setMsg(e?.response?.data?.detail || "Acceso restringido"); setStatus("denied"); });
  }, [user.id, filter, isAdmin]);

  useEffect(() => { if (!detail) load(); }, [load, detail]);

  useEffect(() => {
    if (isAdmin && !detail) getAllTickets(user.id, "").then((d) => setAllTickets(Array.isArray(d) ? d : (d?.tickets || []))).catch(() => {});
  }, [isAdmin, user.id, detail]);

  const openDetail = (id) => { setLoadingDetail(true); setDetail({ id }); getTicket(id, user.id).then(setDetail).catch(() => toast.error("No se pudo abrir")).finally(() => setLoadingDetail(false)); };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try { await postTicketMessage(detail.id, { from_user_id: user.id, body: reply.trim() }); setReply(""); openDetail(detail.id); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error al responder"); }
    finally { setBusy(false); }
  };

  const changeStatus = async (newStatus, assign) => {
    setBusy(true);
    try { await patchTicketStatus(detail.id, { admin_id: user.id, status: newStatus, assign_to_me: !!assign }); toast.success("Ticket actualizado"); openDetail(detail.id); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error al actualizar"); }
    finally { setBusy(false); }
  };

  if (status === "loading") return <Loading />;
  if (status === "denied") return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white py-20 text-center" data-testid="admin-denied">
      <div className="mb-4 rounded-full border border-amber-300 bg-amber-50 p-4 text-amber-700"><ShieldAlert className="h-7 w-7" /></div>
      <h3 className="font-serif text-xl font-bold text-black">Solo administradores</h3>
      <p className="mt-2 max-w-sm text-sm text-slate-500">{msg}</p>
    </div>
  );

  if (detail && detail.subject) {
    return (
      <div className="max-w-3xl space-y-5" data-testid="admin-ticket-detail">
        <button onClick={() => setDetail(null)} className="flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-black"><ArrowLeft className="h-4 w-4" /> Volver</button>
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="font-serif text-xl font-bold text-black">{detail.subject}</div>
              <div className="mt-1 text-sm text-slate-500">{detail.user_name} · {detail.category} {detail.assigned_name && `· Asignado: ${detail.assigned_name}`}</div>
            </div>
            <div className="flex items-center gap-2">
              <select data-testid="ticket-status-select" value={detail.status} onChange={(e) => changeStatus(e.target.value)} disabled={busy} className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
                {Object.entries(TSTATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <button data-testid="ticket-assign-me" disabled={busy} onClick={() => changeStatus(detail.status === "pending" ? "in_progress" : detail.status, true)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold hover:border-black">Asignarme</button>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          {loadingDetail ? <div className="flex justify-center py-8"><span className="h-6 w-6 animate-spin rounded-full border-2 border-slate-200 border-t-black" /></div> :
            (detail.messages || []).map((m) => {
              const mine = m.from_user_id === user.id || m.from_role === "admin" || m.from_role === "staff";
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`admin-msg-${m.id}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${mine ? "bg-black text-white" : "border border-slate-200 bg-slate-50 text-slate-800"}`}>
                    <div className={`mb-0.5 text-[10px] font-semibold uppercase tracking-wider ${mine ? "text-slate-300" : "text-slate-400"}`}>{m.from_name || m.from_role}</div>
                    <div className="whitespace-pre-wrap leading-relaxed">{m.body}</div>
                  </div>
                </div>
              );
            })}
        </div>

        {detail.status !== "closed" && (
          <div className="flex items-end gap-2 border-t border-slate-100 pt-3">
            <textarea data-testid="admin-reply-input" value={reply} onChange={(e) => setReply(e.target.value)} rows={2} placeholder="Responder al artista…" className={`${inputCls} resize-none`} />
            <button data-testid="admin-reply-send" onClick={sendReply} disabled={busy || !reply.trim()} className="shrink-0 rounded-lg bg-black p-3 text-white disabled:opacity-50"><Send className="h-4 w-4" /></button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-support-page">
      <SectionTitle>Soporte · Tickets</SectionTitle>
      <div className="grid grid-cols-3 gap-3" data-testid="support-metrics">
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Tickets abiertos</div>
          <div className="mt-2 font-mono text-2xl font-bold tabular-nums text-amber-600" data-testid="metric-open">{metrics.open}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Resueltos</div>
          <div className="mt-2 font-mono text-2xl font-bold tabular-nums text-emerald-600" data-testid="metric-resolved">{metrics.resolved}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Resp. promedio</div>
          <div className="mt-2 font-mono text-2xl font-bold tabular-nums text-black" data-testid="metric-avg-response">{fmtDur(metrics.avg)}</div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button key={f.key} data-testid={`ticket-filter-${f.key || "all"}`} onClick={() => setFilter(f.key)} className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${filter === f.key ? "border-black bg-black text-white" : "border-slate-300 text-slate-600 hover:border-black"}`}>{f.label}</button>
        ))}
      </div>
      <div className="space-y-2">
        {tickets.map((t) => (
          <button key={t.id} data-testid={`admin-ticket-${t.id}`} onClick={() => openDetail(t.id)} className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-left hover:border-black">
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-black">{t.subject}</div>
              <div className="mt-0.5 text-xs text-slate-500">{t.user_name} · {t.category} · {fmtDate(t.created_at)}</div>
            </div>
            <span className="shrink-0 rounded-full border border-slate-300 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-600">{TSTATUS[t.status] || t.status}</span>
          </button>
        ))}
        {!tickets.length && <div className="flex flex-col items-center py-16 text-slate-400"><Inbox className="mb-2 h-8 w-8" /><p className="text-sm">No hay tickets en este filtro.</p></div>}
      </div>
    </div>
  );
}
